// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import PropTypes from 'prop-types';

// Data
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useRef, useState } from "react";

// API/MODELS
import groupAPI from "services/api/groups";
import clearwaysAPI from "modules/clearways/api";

// Mapbox
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helper
import ClearwaysSidebar from "components/Sidenavs/ClearwaysSidebar";
import { processGroupData } from "utils/group";
import { processEventsData } from "utils/event";
import { useSoftUIController } from "hooks/context";
import { enableMap } from "utils/map";
import ClearwaysGroupsMap from "components/Maps/ClearwaysMaps/Groups";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function ClearwaysGroupsMapView({ map, user, devices }) {
    enableMap(map);

    groupAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, miniNavbar } = controller;

    const [groupPreviewID, setGroupPreviewID] = useState(0);
    const [showDevices, setShowDevices] = useState(false);
    const [hasFittedBounds, setHasFittedBounds] = useState(false);

    const set_padding = 120;
    const getMapPadding = () => ({
        left: deviceLayout === 'mobile' ? set_padding / 2 : (miniNavbar ? 0 : 450) + set_padding,
        right: deviceLayout === 'mobile' ? set_padding / 2 : set_padding,
        top: deviceLayout === 'mobile' ? set_padding / 2 : 50 + set_padding,
        bottom: deviceLayout === 'mobile' ? set_padding / 2 : set_padding
    });

    const groups = useQuery({
        queryKey: ['getAllGroups'],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const events = useQuery({
        queryKey: ['getAllClearways'],
        queryFn: async () => {
            return await clearwaysAPI.getAllClearways();
        }
    });

    const events_data = useMemo(() => {
        return events.data ? processEventsData(events.data) : []
    }, [events.data]);

    const groups_data = useMemo(() => {
        if (typeof groups.data != 'undefined') {
            var groups_data = groups.data.filter((row) => row.type == "clearways").map(function (group) {
                var ret = processGroupData(group, devices);
                return ret;
            }).filter(group => typeof group.parent_id == 'undefined');

            return groups_data;
        }

        return null;
    }, [groups.data, devices]);

    useEffect(() => {
        if (!map.current || !groups.data) return;

        var bounds = new mapboxgl.LngLatBounds();
        let hasCoords = false;

        groups_data.forEach(function (group) {
            group.geojson.forEach(function (feature) {
                feature.geometry.coordinates.forEach(function (coords) {
                    coords.forEach(function (coord) {
                        if (coord[0] !== 0 && coord[1] !== 0) {
                            bounds.extend(coord);
                            hasCoords = true;
                        }
                    })
                })
            })
        });

        if (hasCoords) {
            var options = { padding: getMapPadding(), linear: true, duration: 800 };
            if (hasFittedBounds) {
                options.duration = 300;
            }

            map.current.fitBounds(bounds, options);
            setHasFittedBounds(true);
        }
    }, [map, groups_data, deviceLayout]);

    useEffect(() => {
        if (!map.current || !hasFittedBounds) return;

        map.current.easeTo({
            padding: getMapPadding(),
            duration: 800,
            essential: true
        });
    }, [miniNavbar, deviceLayout]);

    if (groups.isLoading || events.isLoading) {
        return ('Loading...');
    }

    if (groups.error || events.error) {
        return ("An error has occurred");
    }

    if (!groups.data) {
        return ('Loading...');
    }

    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            <ClearwaysGroupsMap {...{ map, user, groups_data, setGroupPreviewID, showDevices }} />
            <ClearwaysSidebar
                user={user}
                groupPreviewID={groupPreviewID}
                events={events_data}
                showDevices={showDevices}
                setShowDevices={setShowDevices}
            />
        </SoftBox>
    );
}

ClearwaysGroupsMapView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    devices: PropTypes.array,
}

export default ClearwaysGroupsMapView;